import { useEffect, useRef, useState } from "react";
import { Download, FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, post } from "@/lib/api";
import { downloadFile, blobError } from "@/components/ExportRunMenu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImportMapStep, IMPORT_FIELDS } from "./ImportMapStep";
import { ImportFixStep } from "./ImportFixStep";
import { ImportReviewStep, ImportDone } from "./ImportReviewStep";

const STEPS = ["Match columns", "Fix rows", "Review"];

const toItems = (rows, mapping) => rows.map((r, index) => ({
  index, skip: false, locksmith_id: null, officer_id: null,
  ...Object.fromEntries(IMPORT_FIELDS.map(([k]) => [k, mapping[k] == null ? "" : String(r[mapping[k]] ?? "")])),
}));

const UploadStep = ({ onParsed }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(xlsx|csv)$/i.test(file.name)) { toast.error("Upload a .xlsx or .csv file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File is too large (max 5 MB)"); return; }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      onParsed((await api.post("/runs/import/parse", body)).data, file.name);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const template = async () => {
    try { await downloadFile("/runs/import/template", {}, "warrant-runs-template.xlsx"); } catch (err) { toast.error(await blobError(err)); }
  };
  return (
    <div className="space-y-4" data-testid="import-upload-step">
      <p className="text-sm text-slate-600">Upload an Excel (.xlsx) or CSV file of addresses, up to 5 MB and 2,000 rows. Only the first sheet is read. Rows with the same date and locksmith become one run; rows with no locksmith become open jobs. Nothing is saved until you press Create runs.</p>
      <input ref={input} type="file" accept=".xlsx,.csv" className="hidden" onChange={pick} data-testid="import-file-input" />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => input.current?.click()} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="import-choose-file-button">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileUp className="mr-1 h-4 w-4" />}Choose file
        </Button>
        <Button variant="outline" onClick={template} data-testid="import-template-button"><Download className="mr-1 h-4 w-4" />Download template</Button>
      </div>
    </div>
  );
};

function usePreview(items, active) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    if (!active || !items) return undefined;
    const n = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      post("/runs/import/preview", { rows: items })
        .then((r) => n === seq.current && setResult(r))
        .catch((e) => n === seq.current && toast.error(errorMessage(e)))
        .finally(() => n === seq.current && setLoading(false));
    }, 400);
    return () => {
      clearTimeout(t);
    };
  }, [items, active]);
  return { result, loading };
}

export const ImportRunsDialog = ({ open, onOpenChange, onCreated }) => {
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [items, setItems] = useState(null);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const { result, loading } = usePreview(items, step > 0 && !done);

  const parsed = (data, name) => { setFile({ ...data, name }); setMapping(data.mapping); setStep(0); };
  const update = (index, patch) => setItems((xs) => xs.map((x) => (x.index === index ? { ...x, ...patch } : x)));
  const created = (res) => { setDone(res); onCreated(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-y-auto sm:max-w-5xl" data-testid="import-runs-dialog">
        <DialogHeader><DialogTitle className="font-display">Import runs{file ? ` from ${file.name}` : ""}</DialogTitle></DialogHeader>
        {file && !done && (
          <ol className="flex flex-wrap gap-2 text-xs font-semibold" data-testid="import-steps">
            {STEPS.map((s, i) => <li key={s} className={`rounded-full px-3 py-1 ${i === step ? "bg-blue-700 text-white" : i < step ? "bg-blue-50 text-blue-800" : "bg-slate-100 text-slate-500"}`}>{i + 1}. {s}</li>)}
          </ol>
        )}
        {!file && <UploadStep onParsed={parsed} />}
        {file && !done && step === 0 && <ImportMapStep file={file} mapping={mapping} setMapping={setMapping} onBack={() => setFile(null)}
          onNext={() => { setItems(toItems(file.rows, mapping)); setStep(1); }} />}
        {file && !done && step === 1 && <ImportFixStep items={items} result={result} loading={loading} update={update} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {file && !done && step === 2 && <ImportReviewStep items={items} result={result} loading={loading} onBack={() => setStep(1)} onCreated={created} />}
        {done && <ImportDone done={done} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
};
